import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/core/permissions';
import { formatDate } from '@/lib/format';
import { useProjects } from '../queries';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ProjectStatusBadge } from './StatusBadges';

/** The class detail page's "Projects" section — this class's projects, with a
 *  create shortcut for staff that lands preselected on the class. */
export function ClassProjectsSection({ classPublicId }: { classPublicId: string }) {
  const { t, i18n } = useTranslation(['projects']);
  const { hasRole } = usePermission();
  const [createOpen, setCreateOpen] = useState(false);
  const query = useProjects({ school_class: classPublicId, page_size: 10 });

  const rows = query.data?.results ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('projects:classSection.title')}</CardTitle>
          <div className="flex flex-wrap gap-2">
            {hasRole('admin', 'teacher') ? (
              <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                <Plus />
                {t('projects:create.button')}
              </Button>
            ) : null}
            <Button size="sm" variant="link" asChild>
              <Link to="/projects">{t('projects:classSection.viewAll')}</Link>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('projects:classSection.empty')}</p>
        ) : (
          <ul className="space-y-1">
            {rows.map(project => (
              <li key={project.public_id}>
                <Link
                  to={`/projects/${project.public_id}`}
                  className="hover:bg-accent flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                >
                  <span className="font-medium">{project.title}</span>
                  <span className="text-muted-foreground">
                    {project.due_at
                      ? t('projects:list.due', {
                          date: formatDate(project.due_at, i18n.language),
                        })
                      : t('projects:list.noDue')}
                  </span>
                  <span className="flex-1" />
                  {project.status ? <ProjectStatusBadge status={project.status} /> : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {hasRole('admin', 'teacher') ? (
        <CreateProjectDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          defaultClass={classPublicId}
        />
      ) : null}
    </Card>
  );
}
